import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { WELLBEING_LIMITS, WELLBEING_SCENARIOS } from '../constants/wellbeingMessages'
import {
	detectOpenModal,
	evaluateWellbeingTrigger,
	isActiveCounsellingPath,
} from '../utils/wellbeingScheduler'
import {
	hasCompletedToday,
	markWellbeingCompleted,
	markWellbeingDismissed,
	markWellbeingShown,
	writeWellbeingState,
} from '../utils/wellbeingStorage'
import { deriveSessionStats } from '../../dashboardGreeting/utils/greetingEngine'
import { fetchScheduledMeetings } from '../../../utils/counsllingApi'
import { getUserFromLocalStorage } from '../../../utils/utils'

/**
 * Idle-based wellbeing scheduler (no polling loops).
 * Arms a 30s idle timer; on fire, evaluates scenario once.
 */
export function useWellbeingScheduler({ enabled = true } = {}) {
	const location = useLocation()
	const [prompt, setPrompt] = useState(null)
	const [toast, setToast] = useState(null)
	const [sessions, setSessions] = useState([])
	const idleTimerRef = useRef(null)
	const armedRef = useRef(true)

	const user = useMemo(() => getUserFromLocalStorage(), [])

	useEffect(() => {
		if (!enabled) return undefined
		let cancelled = false
		;(async () => {
			try {
				const counsellorId = user?._id || user?.id || user?.profile?._id
				const result = await fetchScheduledMeetings({
					limit: 40,
					status: 'all',
					...(counsellorId ? { counsellorId: String(counsellorId) } : {}),
				})
				if (!cancelled) setSessions(result.sessions || [])
			} catch {
				if (!cancelled) setSessions([])
			}
		})()
		return () => {
			cancelled = true
		}
	}, [enabled, user])

	const stats = useMemo(() => deriveSessionStats(sessions, new Date()), [sessions])

	const clearIdle = useCallback(() => {
		if (idleTimerRef.current) {
			clearTimeout(idleTimerRef.current)
			idleTimerRef.current = null
		}
	}, [])

	const tryShow = useCallback(() => {
		if (!enabled || !armedRef.current) return
		if (prompt) return

		const hasActiveSession =
			isActiveCounsellingPath(location.pathname) ||
			Boolean(sessionStorage.getItem('mypeegu_active_counselling'))

		const hasOpenModal = detectOpenModal(['wellbeing-modal', 'wellbeing-title'])

		const now = new Date()
		const betweenSessions =
			stats.completedSessions > 0 && stats.remainingSessions > 0

		const result = evaluateWellbeingTrigger({
			now,
			pathname: location.pathname,
			isIdle: true,
			hasOpenModal,
			hasActiveSession,
			sessionsBeforeLunchCompleted: stats.todaysSessions.filter((s) => {
				const noon = new Date(now)
				noon.setHours(12, 0, 0, 0)
				return s._start < noon && s._start < now
			}).length,
			todaysSessionCount: stats.todaysSessions.length,
			remainingSessions: stats.remainingSessions,
			betweenSessions,
		})

		if (!result) return

		armedRef.current = false
		const shown = markWellbeingShown(result.scenario)
		if (result.meta?.riskSessionId) {
			writeWellbeingState({
				...shown,
				riskShownForId: result.meta.riskSessionId,
			})
		}
		setPrompt({
			scenario: result.scenario,
			openedAt: Date.now(),
		})
	}, [enabled, prompt, location.pathname, stats])

	const armIdle = useCallback(() => {
		if (!enabled) return
		clearIdle()
		idleTimerRef.current = setTimeout(() => {
			tryShow()
		}, WELLBEING_LIMITS.IDLE_MS)
	}, [enabled, clearIdle, tryShow])

	useEffect(() => {
		if (!enabled) return undefined
		armedRef.current = true
		armIdle()

		const onActivity = () => {
			if (prompt) return
			armedRef.current = true
			armIdle()
		}

		const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll']
		events.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }))
		document.addEventListener('visibilitychange', onActivity)

		return () => {
			clearIdle()
			events.forEach((evt) => window.removeEventListener(evt, onActivity))
			document.removeEventListener('visibilitychange', onActivity)
		}
	}, [enabled, armIdle, clearIdle, prompt, location.pathname])

	const openManual = useCallback((scenario = WELLBEING_SCENARIOS.MANUAL) => {
		setToast(null)
		setPrompt({ scenario, openedAt: Date.now(), manual: true })
	}, [])

	const closeWithToast = useCallback(() => {
		setPrompt(null)
		setToast({
			id: Date.now(),
			message: 'Breathing break is always available from Help Center → Wellbeing.',
		})
		armedRef.current = true
		armIdle()
	}, [armIdle])

	const dismiss = useCallback(() => {
		markWellbeingDismissed()
		closeWithToast()
	}, [closeWithToast])

	const remindLater = useCallback(() => {
		markWellbeingDismissed()
		closeWithToast()
	}, [closeWithToast])

	const complete = useCallback(() => {
		markWellbeingCompleted()
		setPrompt(null)
		armedRef.current = true
		armIdle()
	}, [armIdle])

	const continuePlatform = useCallback(() => {
		markWellbeingDismissed()
		setPrompt(null)
		setToast({
			id: Date.now(),
			message: 'Breathing break is always available from Help Center → Wellbeing.',
		})
		armedRef.current = true
		armIdle()
	}, [armIdle])

	const clearToast = useCallback(() => setToast(null), [])

	return {
		prompt,
		toast,
		openManual,
		dismiss,
		remindLater,
		complete,
		continuePlatform,
		clearToast,
		breathingAvailable: !hasCompletedToday(),
		completedToday: hasCompletedToday(),
	}
}
